class ProductAddOns extends HTMLElement {
  connectedCallback() {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.abortController = new AbortController();
    this.list = this.querySelector('[data-product-add-ons-list]');
    this.recommendationsContainer = this.querySelector('[data-product-add-ons-recommendations]');
    this.statusElement = this.querySelector('[data-product-add-ons-status]');
    this.giftWrapInput = this.querySelector('[data-add-on-role="gift_wrap"]');
    this.giftMessageContainer = this.querySelector('[data-gift-message-container]');
    this.giftMessageInput = this.querySelector('[data-gift-message-input]');
    this.form = this.findProductForm();

    this.addEventListener('change', (event) => {
      if (event.target.matches('.product-add-ons__checkbox')) {
        this.setStatus('');

        if (event.target === this.giftWrapInput) {
          this.syncGiftMessageVisibility();
        }
      }
    }, { signal: this.abortController.signal });

    this.form?.addEventListener('submit', (event) => this.onProductFormSubmit(event), {
      capture: true,
      signal: this.abortController.signal
    });

    this.syncGiftMessageVisibility();
    this.loadFallbackRecommendations();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get selectedInputs() {
    return Array.from(this.querySelectorAll('.product-add-ons__checkbox:checked:not(:disabled)'));
  }

  get selectableInputs() {
    return Array.from(this.querySelectorAll('.product-add-ons__checkbox:not(:disabled)'));
  }

  findProductForm() {
    if (this.dataset.productFormId) {
      return document.getElementById(this.dataset.productFormId);
    }

    return this.closest('product-form')?.querySelector('form[action*="/cart/add"]')
      || document.querySelector('product-form form[action*="/cart/add"]');
  }

  async loadFallbackRecommendations() {
    if (this.dataset.useRecommendations !== 'true' || this.dataset.curatedCount !== '0') {
      return;
    }

    const productId = this.dataset.productId;

    if (!productId || !this.recommendationsContainer) {
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const limit = Number.parseInt(this.dataset.recommendationsLimit || '4', 10);
    const hiddenHandles = new Set((this.dataset.hiddenHandles || '').split(',').filter(Boolean));
    hiddenHandles.add(this.dataset.productHandle);

    try {
      const response = await fetch(`${root}recommendations/products.json?product_id=${productId}&limit=${limit}&intent=related`, {
        headers: { Accept: 'application/json' },
        priority: 'low'
      });

      if (!response.ok) {
        throw new Error('Recommendations request failed');
      }

      const payload = await response.json();
      const products = Array.isArray(payload.products) ? payload.products : [];
      const fragment = document.createDocumentFragment();

      products.forEach((recommendedProduct) => {
        if (!recommendedProduct?.handle || hiddenHandles.has(recommendedProduct.handle) || recommendedProduct.available === false) {
          return;
        }

        const variant = this.findRecommendedVariant(recommendedProduct);

        if (!variant?.id || variant.available === false) {
          return;
        }

        fragment.append(this.createRecommendedRow(recommendedProduct, variant));
        hiddenHandles.add(recommendedProduct.handle);
      });

      if (fragment.childElementCount > 0) {
        this.recommendationsContainer.hidden = false;
        this.recommendationsContainer.append(fragment);
      } else if (this.selectableInputs.length === 0) {
        this.hidden = true;
      }
    } catch (error) {
      if (this.selectableInputs.length === 0) {
        this.hidden = true;
      }
    }
  }

  findRecommendedVariant(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];

    return variants.find((variant) => variant.available !== false) || variants[0];
  }

  createRecommendedRow(product, variant) {
    const label = document.createElement('label');
    label.className = 'product-add-ons__item';
    label.setAttribute('data-product-add-ons-item', '');

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.className = 'product-add-ons__checkbox';
    input.value = String(variant.id);
    input.dataset.addOnId = String(variant.id);
    input.dataset.addOnTitle = product.title || '';
    input.dataset.addOnHandle = product.handle || '';
    input.dataset.addOnRole = 'recommendation';

    const media = document.createElement('span');
    media.className = 'product-add-ons__media';
    media.setAttribute('aria-hidden', 'true');

    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'product-add-ons__image';
      image.loading = 'lazy';
      image.alt = product.title || '';
      image.src = product.featured_image;
      media.append(image);
    }

    const content = document.createElement('span');
    content.className = 'product-add-ons__content';

    const title = document.createElement('span');
    title.className = 'product-add-ons__title';
    title.textContent = product.title || '';
    content.append(title);

    if (variant.title && variant.title !== 'Default Title') {
      const variantTitle = document.createElement('span');
      variantTitle.className = 'product-add-ons__description';
      variantTitle.textContent = variant.title;
      content.append(variantTitle);
    }

    const price = document.createElement('span');
    price.className = 'product-add-ons__price';
    price.textContent = this.formatMoney(variant.price ?? product.price ?? product.price_min ?? 0);

    label.append(input, media, content, price);

    return label;
  }

  onProductFormSubmit(event) {
    const selectedInputs = this.selectedInputs;

    if (selectedInputs.length === 0 || !this.form || event.target !== this.form || event.defaultPrevented) {
      return;
    }

    if (event.submitter?.getAttribute('aria-busy') === 'true') {
      return;
    }

    if (!this.form.checkValidity()) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    this.addProductAndSelectedItems(event.submitter);
  }

  async addProductAndSelectedItems() {
    const selectedInputs = this.selectedInputs;

    if (selectedInputs.length === 0 || !this.form) {
      return;
    }

    this.setBusy(true);
    this.setStatus('');
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    try {
      const formData = new FormData(this.form);
      const bundleId = `addons-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const mainItem = this.buildMainCartItem(formData, bundleId);
      const addOnItems = selectedInputs.map((input) => this.buildAddOnCartItem(input, bundleId));
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [mainItem, ...addOnItems],
          sections: sectionsToBundle.join(','),
          sections_url: `${root}variants/${mainItem.id}`
        })
      });

      const responseJson = await this.parseJsonResponse(response);

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || 'Unable to add selected add-ons.');
      }

      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${root}cart`;
        return;
      }

      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' }
      });
      const cart = await this.parseJsonResponse(cartResponse);

      if (!cartResponse.ok) {
        throw new Error('Unable to refresh the cart. Please try again.');
      }

      cart.sections = responseJson.sections;

      this.form.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: responseJson.items || [responseJson],
          cart,
          onSuccessDo: new FormData(this.form).get('on_success')
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          cart,
          onSuccessDo: 'force_open_drawer'
        }
      }));

      selectedInputs.forEach((input) => {
        input.checked = false;
      });
      if (this.giftMessageInput) {
        this.giftMessageInput.value = '';
      }
      this.syncGiftMessageVisibility();
      this.setStatus('Selected add-ons were added with this product.');
    } catch (error) {
      this.setStatus(error.message || 'Unable to add selected add-ons.', true);
      this.form?.dispatchEvent(new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: error.message || 'Unable to add selected add-ons.' }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this.setBusy(false);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  buildMainCartItem(formData, bundleId) {
    const id = Number(formData.get('id'));
    const quantity = Math.max(1, Number.parseInt(formData.get('quantity') || '1', 10) || 1);
    const properties = this.extractProperties(formData);

    if (!id) {
      throw new Error('Please select an available product option.');
    }

    properties._addon_bundle_id = bundleId;

    const item = { id, quantity, properties };
    const sellingPlan = formData.get('selling_plan');

    if (sellingPlan) {
      item.selling_plan = sellingPlan;
    }

    return item;
  }

  buildAddOnCartItem(input, bundleId) {
    const id = Number(input.dataset.addOnId || input.value);
    const role = input.dataset.addOnRole || 'add_on';

    if (!id) {
      throw new Error('One selected add-on is unavailable.');
    }

    const properties = {
      _parent_product: this.dataset.productTitle || '',
      _parent_handle: this.dataset.productHandle || '',
      _parent_bundle_id: bundleId,
      _add_on_role: role
    };

    if (role === 'gift_wrap') {
      const giftMessage = this.giftMessageInput?.value.trim().slice(0, 250);

      if (giftMessage) {
        properties['Gift Message'] = giftMessage;
      }
    }

    return {
      id,
      quantity: 1,
      properties
    };
  }

  syncGiftMessageVisibility() {
    const isSelected = Boolean(this.giftWrapInput?.checked);

    if (this.giftMessageContainer) {
      this.giftMessageContainer.hidden = !isSelected;
    }

    this.giftWrapInput?.setAttribute('aria-expanded', String(isSelected));

    if (!isSelected && this.giftMessageInput) {
      this.giftMessageInput.value = '';
    }
  }

  extractProperties(formData) {
    const properties = {};

    formData.forEach((value, name) => {
      const match = name.match(/^properties\[(.+)]$/);

      if (!match || value === '') {
        return;
      }

      if (value instanceof File) {
        if (value.size > 0) {
          throw new Error('Please add this product first, then add selected add-ons from the cart.');
        }

        return;
      }

      properties[match[1]] = value;
    });

    return properties;
  }

  async parseJsonResponse(response) {
    const text = await response.text();

    try {
      return JSON.parse(text || '{}');
    } catch (error) {
      return {};
    }
  }

  setBusy(isBusy) {
    this.toggleAttribute('aria-busy', isBusy);
    this.querySelectorAll('.product-add-ons__checkbox').forEach((element) => {
      element.disabled = isBusy;
    });
    this.giftMessageInput?.toggleAttribute('disabled', isBusy);

    Array.from(this.form?.elements || []).forEach((element) => {
      if (element.type === 'submit') {
        element.toggleAttribute('aria-busy', isBusy);
      }
    });
  }

  setStatus(message, isError = false) {
    if (!this.statusElement) {
      return;
    }

    this.statusElement.textContent = message;
    this.statusElement.classList.toggle('product-add-ons__status--error', isError);
  }

  formatMoney(value) {
    const numericValue = Number(value) || 0;
    const cents = Number.isInteger(numericValue) ? numericValue : Math.round(numericValue * 100);
    const amount = (cents / 100).toFixed(2);
    const format = window.themeVariables?.settings?.currencyCodeEnabled
      ? window.themeVariables?.settings?.moneyWithCurrencyFormat
      : window.themeVariables?.settings?.moneyFormat;

    if (!format) {
      return `$${amount}`;
    }

    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
      .replace(/\{\{\s*amount\s*\}\}/, amount);
  }
}

if (!window.customElements.get('product-add-ons')) {
  window.customElements.define('product-add-ons', ProductAddOns);
}

class CartAddOnsCleaner {
  constructor() {
    this.isCleaning = false;
    document.addEventListener('cart:change', (event) => this.cleanup(event.detail?.cart));

    if (window.themeVariables?.settings?.pageType === 'cart') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.cleanup());
      } else {
        this.cleanup();
      }
    }
  }

  async cleanup(cart = null) {
    if (this.isCleaning) {
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const currentCart = cart || await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' }
    }).then((response) => response.json());

    const parentBundleIds = new Set();
    const orphanLineKeys = [];

    currentCart.items.forEach((item) => {
      const properties = item.properties || {};

      if (properties._addon_bundle_id) {
        parentBundleIds.add(properties._addon_bundle_id);
      }
    });

    currentCart.items.forEach((item) => {
      const properties = item.properties || {};

      if (properties._parent_bundle_id && !parentBundleIds.has(properties._parent_bundle_id)) {
        orphanLineKeys.push(item.key);
      }
    });

    if (orphanLineKeys.length === 0) {
      return;
    }

    this.isCleaning = true;

    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const updates = {};
      orphanLineKeys.forEach((lineKey) => {
        updates[lineKey] = 0;
      });

      const response = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          updates,
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname
        })
      });

      if (!response.ok) {
        return;
      }

      const responseText = await response.text();
      let updatedCart;

      try {
        updatedCart = JSON.parse(responseText || '{}');
      } catch (error) {
        return;
      }

      if (window.themeVariables?.settings?.pageType === 'cart') {
        window.location.reload();
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'line-item:change',
          cart: updatedCart
        }
      }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this.isCleaning = false;
    }
  }
}

if (!window.__productAddOnsCleaner) {
  window.__productAddOnsCleaner = new CartAddOnsCleaner();
}
